// The read shapes S11's three screens draw, as the lane's producers publish them (PARALLEL-MAP §2): the PlanView
// `assessment` and `rounds` of `intake.plan_open` (S04), the ExerciseOut additions of `exercise.open` (S09), and the
// `interview_framework:<level>` payload (S05). The generated client names the tools and their top-level outputs; a
// nested object it types loosely (a `Record<string, unknown>`) is read through these shapes, so the screens compile
// against the published fields whichever way the generator spells the nesting. # REBASE: when the lane's build pass has
// regenerated the client, a shape the generator now types exactly may be replaced by the generated type.
//
// Also: a round's name from its kind and sequence (never the stored `label`, which stays "Onsite Round n" in data for
// packets and V1 walks), the display word of each assessment status, and who may see or edit the framework page.

import { ASSESSMENT_STATUS, G1_LABELS, LABEL_TEMPLATES, type ErrorStripProps, type Level } from "@/mc";
import { AppError } from "@/api/runtime";

// ---- the interview plan (S04 PlanView) ------------------------------------------------------------------------------

export interface PlanPersonLike {
  person_id: string;
  name: string;
  initials?: string | undefined;
}

export interface PlanRoundLike {
  id: string;
  key: string;
  kind: string;
  sequence: number;
  in_use: boolean;
}

export type AssessmentDefault = "optional" | "recommended" | "required";

export interface PlanAssessmentLike {
  decided?: boolean | null | undefined;
  required: boolean | null;
  assessment_type?: string | null | undefined;
  stage_round_key?: string | null | undefined;
  due_window_days?: number | null | undefined;
  owner?: PlanPersonLike | null | undefined;
  reviewers?: readonly PlanPersonLike[] | null | undefined;
  criteria?: readonly string[] | null | undefined;
  brief_text?: string | null | undefined;
  reason_when_no?: string | null | undefined;
  recommendations_text?: string | null | undefined;
  brief_version?: number | null | undefined;
  suggested_default?: AssessmentDefault | null | undefined;
}

export type PlanProjection = "full" | "consult" | "scheduling";

export interface PlanCanLike {
  edit?: boolean | undefined;
  finalize?: boolean | undefined;
  reopen?: boolean | undefined;
}

export interface PlanViewLike {
  requisition_id: string;
  level?: string | null | undefined;
  projection: PlanProjection;
  plan_status?: string | null | undefined;
  plan_revision: number;
  rounds?: readonly PlanRoundLike[] | null | undefined;
  assessment?: PlanAssessmentLike | null | undefined;
  finalized_by?: { person_id: string; name: string; seat?: string | null | undefined; at?: string | null | undefined } | null | undefined;
  can?: PlanCanLike | null | undefined;
}

/** The plan as the card reads it: the generated output, seen through the published shape. */
export function planView(plan: unknown): PlanViewLike {
  return plan as PlanViewLike;
}

// ---- the candidate's assessment (S09 ExerciseOut) -------------------------------------------------------------------

/** The keys of `ASSESSMENT_DISPLAY` (D13 display mapping). */
export type AssessmentDisplay =
  | "undecided" | "not_required" | "required" | "draft" | "sent" | "submitted" | "reviewed" | "completed" | "withdrawn";

export type ExerciseProjection = "full" | "reviewer" | "status" | "brief";

export type Finding = "met" | "partial" | "not";

export interface CriterionFinding {
  criterion: string;
  finding: Finding;
  note?: string | null | undefined;
}

export interface ReviewLike {
  reviewer?: { person_id?: string | undefined; name: string } | string | null | undefined;
  criteria_findings: readonly CriterionFinding[];
  evaluation_text: string;
  advisory_rating?: number | null | undefined;
  submitted_at: string;
}

export interface ReviewerLike {
  person_id: string;
  name: string;
  reviewed_at?: string | null | undefined;
}

export interface ExerciseViewLike {
  application_id: string;
  requisition_id: string;
  projection: ExerciseProjection;
  status: string;
  display: AssessmentDisplay;
  display_reason?: string | null | undefined;
  required?: boolean | null | undefined;
  assessment_type?: string | null | undefined;
  owner?: { person_id: string; name: string } | null | undefined;
  stage_round_key?: string | null | undefined;
  criteria?: readonly string[] | null | undefined;
  brief_text?: string | null | undefined;
  brief_version?: number | null | undefined;
  sent_at?: string | null | undefined;
  due_at?: string | null | undefined;
  returned_at?: string | null | undefined;
  return_file_ids?: readonly string[] | null | undefined;
  completed_at?: string | null | undefined;
  completion_outcome?: string | null | undefined;
  withdrawn_at?: string | null | undefined;
  withdrawal_reason?: string | null | undefined;
  reviewers?: readonly ReviewerLike[] | null | undefined;
  reviews_in?: number | null | undefined;
  reviews_total?: number | null | undefined;
  my_review?: ReviewLike | null | undefined;
  reviews?: readonly ReviewLike[] | null | undefined;
  can?: Readonly<Record<string, boolean>> | null | undefined;
}

export function exerciseView(data: unknown): ExerciseViewLike {
  return data as ExerciseViewLike;
}

/** His word for each display key (G §6 chain); `undecided` draws none (the intake decision is still open). */
export const DISPLAY_WORD: Readonly<Record<AssessmentDisplay, string | null>> = {
  undecided: null,
  not_required: ASSESSMENT_STATUS.notRequired,
  required: ASSESSMENT_STATUS.required,
  draft: ASSESSMENT_STATUS.draft,
  sent: ASSESSMENT_STATUS.sent,
  submitted: ASSESSMENT_STATUS.submitted,
  reviewed: ASSESSMENT_STATUS.reviewed,
  completed: ASSESSMENT_STATUS.completed,
  withdrawn: ASSESSMENT_STATUS.withdrawn,
};

/** The chain's five steps, in his order; Withdrawn is the terminal branch that replaces the last. */
export const CHAIN: readonly AssessmentDisplay[] = ["draft", "sent", "submitted", "reviewed", "completed"];

/** Others' reviews reach a reviewer only once every review is in (D13 reviewer projection). */
export function reviewsVisible(projection: ExerciseProjection, display: AssessmentDisplay): boolean {
  if (projection === "full") return true;
  if (projection === "reviewer") return display === "reviewed" || display === "completed";
  return false;
}

/** Returned work and the completion outcome are evaluative content: never in the status or brief projection. */
export function evaluativeVisible(projection: ExerciseProjection): boolean {
  return projection === "full" || projection === "reviewer";
}

// ---- the framework (S05 InterviewFrameworkShape) --------------------------------------------------------------------

export interface FrameworkRoundLike {
  kind: string;
  sequence: number;
  label: string;
  format: string;
  duration_minutes: number;
  in_use: boolean;
  suggested_values: readonly string[];
  suggested_skill_numbers: readonly number[];
  executive_participation: boolean;
}

export interface FrameworkShapeLike {
  level?: string | undefined;
  requires_all_roots: boolean;
  rounds: readonly FrameworkRoundLike[];
  assessment_default: AssessmentDefault;
  note?: string | undefined;
}

export interface LevelLimitsLike {
  min_skills: number;
  max_skills: number;
}

/** Who may see the framework page: the Admin proposes (business configuration, DC-228), a reader of configuration sees
 * values, every other seat has no page. The server decides again on every call (config.propose's domain seat). */
export type FrameworkAccess = "edit" | "view" | "none";

export function frameworkAccess(roles: readonly string[], can: (tool: string) => boolean): FrameworkAccess {
  const admin = roles.includes("admin");
  if (admin && can("config.propose") && can("config.list")) return "edit";
  if (can("config.list") && can("config.open")) return "view";
  return "none";
}

export const LEVELS: readonly Level[] = ["manager_and_below", "director_srdirector", "vp_and_above"];

export function isLevel(value: string | null | undefined): value is Level {
  return value !== null && value !== undefined && (LEVELS as readonly string[]).includes(value);
}

// ---- round names --------------------------------------------------------------------------------------------------

/** A round's name from its kind and sequence: Recruiter screen · HM interview · Final Interview Loop · Round n. */
export function roundName(kind: string, sequence: number): string | null {
  if (kind === "recruiter_screen") return G1_LABELS.recruiterScreen;
  if (kind === "hm_round") return G1_LABELS.hmInterview;
  if (kind === "onsite") return LABEL_TEMPLATES.finalLoopRound({ n: sequence });
  return null;
}

/** The same name from a round key (`recruiter_screen`, `hm_round`, `onsite_<n>`). */
export function roundNameFromKey(key: string | null | undefined): string | null {
  if (!key) return null;
  if (key === "recruiter_screen" || key === "hm_round") return roundName(key, 1);
  const match = /^([a-z]+)_(\d+)$/.exec(key);
  return match ? roundName(match[1] ?? "", Number(match[2])) : null;
}

/** The stable key of a framework round, as the plan keys its rounds. */
export function frameworkRoundKey(kind: string, sequence: number): string {
  return kind === "recruiter_screen" || kind === "hm_round" ? kind : `${kind}_${sequence}`;
}

// ---- the people a pick offers (S04 panel.people: no email) ---------------------------------------------------------

export interface PlanPickPerson {
  id: string;
  name: string;
  initials?: string | undefined;
  role_words?: readonly string[] | undefined;
}

export function peopleOf(output: unknown): readonly PlanPickPerson[] {
  return (output as { people?: readonly PlanPickPerson[] } | null | undefined)?.people ?? [];
}

// ---- refused, or failed (DC-24) -----------------------------------------------------------------------------------

/** A write's error as the card draws it: a refusal (4xx: his rule sentence, values kept) or a failure (the error strip
 * with the correlation id and Retry). */
export function failureOf(error: unknown, onRetry?: (() => void) | undefined): { refused?: string | undefined; failure?: ErrorStripProps | undefined } {
  if (!error) return {};
  if (error instanceof AppError && error.status >= 400 && error.status < 500) {
    return { refused: error.remedy && error.remedy !== error.message ? `${error.message} ${error.remedy}` : error.message };
  }
  const message = error instanceof Error ? error.message : String(error);
  const problem = error instanceof AppError
    ? { status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId }
    : undefined;
  return { failure: { message, problem, onRetry } };
}
